/**
 * Workspace component handles rendering workspace settings and subscription details.
 *
 * Allows editing of general settings like organization name and message ID values.
 * Renders team settings to manage workspace users.
 * Shows subscription details and handles purchasing additional seats.
 * Handles inviting and removing users, updating permissions.
 * Fetches product info from Stripe to display pricing.
 */
import {useEffect, useState} from "react";
import { UserGroupIcon, ExclamationCircleIcon, AdjustmentsVerticalIcon, CalendarIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import InputBox from "../components/Base/Elements/Input/InputBox";
import InnerNavButton from "../components/Base/Elements/Nav/InnerNavButton";
import InnerNavButtonSelected from "../components/Base/Elements/Nav/InnerNavButtonSelected";
import DuplicateUserAlert from "../components/Base/Elements/Alerts/DuplicateUserAlert";
import AccountUserLimitAlert from "../components/Base/Elements/Alerts/AccountUserLimitAlert";
import PasswordNotComplexAlert from "../components/Base/Elements/Alerts/PasswordNotComplexAlert";
import EmailFormatAlert from "../components/Base/Elements/Alerts/EmailFormatAlert";
import UserPermissionsModal from "../components/Accounts/Elements/UserPermissionsModal";
import PricingBox from "../components/Accounts/Elements/PricingBox";
import useFetch from 'use-http';
import ScreenShot from '../../images/screenshot.png';
import Environments from "../components/Environments/Environments";
import logo from '../../images/retrohook.png';

const awsProductURL  = 'https://aws.amazon.com/marketplace/pp/prodview-boo4ccgoad3ae';
const basePermissions = {
  dashboard: { read: Boolean(true) },
  workflows: { create: false, read: false, update: false, delete: false },
  tunnels: { create: false, read: false, delete: false },
  workspace: { admin: false },
};
const newInvite = { firstName: '', lastName: '', email: '', tempPass: '', permissions: basePermissions };

export default function Workspace(props : any) {
  const { post, get, response, loading } = useFetch();
  const [subUx, setSubUx] = useState('Settings');
  const [error, setError] = useState(null);
  const [products, setProducts] = useState([]);
  const [contactModal, setContactModal] = useState(false);
  const [paymentModal, setPaymentModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [togglePermissions, setTogglePermissions] = useState(false);
  const [selectedUser, setSelectedUser] = useState < any > ({permissions: basePermissions, email: '', name: '', id: ''});
  const [invite, setInvite] = useState < any > (newInvite);
  const [customer, setCustomer] = useState({});
  const [billingUX, setBillingUX] = useState('');
  const [loadingType, setLoadingType] = useState('');
  const [quantityPurchased, setQuantityPurchased] = useState(0);
  const [workspace, setWorkspace ] = useState<any>(props.selectedWorkspace); 
  
  useEffect(() => { getProducts(); setWorkspace(props.selectedWorkspace); }, [props.selectedWorkspace]);

  const getProducts = async () => {
    const productItems: any = await get(`account/get-stripe-products/${props.user.account.PK}`);
    if (productItems?.data) {
      const items = productItems.data;
      const promises = items.map((product : any) => {
        if (product.product[0].name == 'Production Plan' && product.product[0].active) {
        product.features = [
          'Everything in Developer Plan +',
          'Create unlimited workflows for streamlined legacy healthcare events.',
          'Invite unlimited team members to collaborate in a shared, efficient workspace.',
          'Simple AWS Deployment: Deploy Retrohook into your AWS account with ease.',
          'Production Environment: Create a single production environment for your organization. (more can be purchased)',
          'Secure Connectivity: Generate and establish up to 5 IPSEC VPN Tunnels for robust and secure remote connections.',
        ];
      } else if (product.product[0].name == 'Developer Plan' && product.product[0].active) {
        product.features = [
          'Effortless Serverless Healthcare Messaging: Experience seamless hl7v2 communication without the complexities of server management',
          'Test Workflows: Design and test up to 5 non-PHI workflows, perfect for experimentation and learning.',
          'Developer Workspace: Collaborate effectively with a 3-seat workspace designed for dynamic development teams.',
          'Message Allocation: Benefit from 100 messages per month, allowing for ample testing and development.',
          'Community Support: Gain insights and assistance from a community that understands your development needs.'
        ];
      }
        return product;
      });
      const products: any = await Promise.all(promises).then(res => {
        return res;
      }).catch(error => {
        console.error(error);
      });
      const filtered = products.filter((product : any) => (product.product[0].active != false && product.active != false));
      console.dir(filtered);
      setProducts(filtered.sort((a : any, b : any) => (a.unit_amount > b.unit_amount) ? 1 : -1));
    }
  }
  const selectProduct = async (product : any, quantityPurchased: number) => {
    if (product.product[0].name != 'Enterprise Plan' && product.product[0].name != 'Developer Plan') {
      if (props.account.type != 'aws') {
        await checkoutStripe(product.id, quantityPurchased);
        setSelectedProduct(product);
        setPaymentModal(!paymentModal);
      } else {
        window.location.replace(awsProductURL);
      }
    }
    if (product.product[0].name == 'Enterprise Plan') {
      setSelectedProduct(product);
      setContactModal(!contactModal);
    }
  }
  const getStripeCustomer = async () => {
    const customer: any = await get(`account/get-stripe-customer/${props.account.customerId}`);
    if (response.ok) {
      return customer.customer;
    }
  }
  const getCustomerPortal = async () => {
    if (props.account.customerIdentifier) {
      if (props.account.type == 'stripe') {
        const customerPortal: any = await get(`account/generate-stripe-customer-portal-session/${props.account.customerIdentifier}`);
        if (response.ok) window.location.replace(customerPortal.portalSession.url);
      }
      if (props.account.type == 'aws') {
        window.location.replace(awsProductURL);
      }
    }
  }
  const createStripeCustomer = async () => {
    const body = { email: props.selectedWorkspace.email };
    const newCustomer: any = await post(`account/create-stripe-customer/${props.user.attributes.id}`, body);
    if (response.ok) {
      return newCustomer.customer;
    }
  }
  const checkoutStripe = async (priceId : string, quantityPurchased: number) => {
    let customerId = props.account.customerId;
    if (props.account.customerId) {
      const customer = await getStripeCustomer();
      customerId = customer.id;
      setCustomer(customer);
    } else {
      const customer = await createStripeCustomer();
      customerId = customer.id;
      setCustomer(customer);
    }
    const body = { priceId, quantityPurchased: quantityPurchased, customerId, account: props.account };
    const checkout: any = await post(`account/generate-stripe-checkout-session/${props.user.attributes.id}`, body);
    if (response.ok) {
      window.location.replace(checkout.session.url);
    }
  }
  const handleNewUserFirstNameChange = (event : any) => {
    const updated = { ...invite, firstName: event.target.value };
    setInvite(updated);
  }
  const handleNewUserLastNameChange = (event : any) => {
    const updated = { ...invite, lastName: event.target.value };
    setInvite(updated);
  }
  const handleNewUserEmailChange = (event : any) => {
    const updated = { ...invite, email: event.target.value };
    setInvite(updated);
  }
  const handleMessageIDValueChange = (event : any) => {
    const updated = { ...workspace, hash: { ...workspace.hash, value: event.target.value }};
    setWorkspace(updated);
  }
  const handleMessageIDChange = (event : any) => {
    const updated = { ...workspace, hash: { ...workspace.hash, location: event.target.value }};
    setWorkspace(updated);
  }
  const handleOrganizationValueChange = (event : any) => {
    console.dir(event.target.value);
    const updated = { ...workspace, organization: event.target.value, name: event.target.value };
    setWorkspace(updated);
  }
  const openPermissions = (user : any) => {
    setSelectedUser(user);
    setTogglePermissions(true);
  }
  const closePermissions = () => {
    setTogglePermissions(false);
    setSelectedUser({permissions: basePermissions, email: '', name: '', id: ''});
  }
  const clearInvite = () => {
    const invite: any = {
      firstName: "",
      lastName: '',
      email: '',
      tempPass: '',
      permissions: basePermissions
    };
    setInvite(invite);
  }
  const addUserToWorkspace = async (slug : string) => {
    if (!invite.firstName || !invite.lastName || !invite.email) {
      const error: any = 'MissingInviteFields';
      setError(error);
      return;
    }
    invite.tempPass = `${(Math.random() + 1).toString(36).substring(6)}-HL7!`;
    const body = { workspace, invite, account: props.account };
    let usersMax;
    if (workspace.quantityPurchased < 1) usersMax = 5; else usersMax = workspace.quantityPurchased * 10;
    if (workspace.accounts.length >= usersMax) {
      const error: any = 'AccountUserLimitAlert';
      setError(error);
    } else {
      const newAccount: any = await post(`account/workspace-invite/${slug}`, body);
      if (newAccount) {
        setWorkspace(newAccount.workspace); 
        props.setAccount(newAccount.account);
      }
    } 
    setLoadingType('');
    clearInvite();
  }
  const removeUserFromWorkspace = async (selectedUser : any) => {
    const params = {selectedUser, workspaceParams: workspace, account: props.account};
    const response = await post(`account/workspace-remove/${props.user.attributes.id}`, params);
    if (response) { 
      props.setSelectedWorkspace(response.data);
      setWorkspace(response.data);
    }
  }
  const updatePermissions = (resource : any, permission : any) => {
    selectedUser.permissions[resource][permission] = !JSON.parse(selectedUser.permissions[resource][permission]);
    const accounts = workspace.accounts.map((account : any) => {
      if (account.email == selectedUser.email) {
        if (account.permissions[resource][permission] == true || account.permissions[resource][permission] == 'true') {
          account.permissions[resource][permission] = true;
        } else {
          account.permissions[resource][permission] = false;
        }
      }
      return account;
    });
    setWorkspace({ ...workspace, accounts });
  }
  const savePermissions = async () => {
    const response = await post(`account/update-workspace/${props.selectedWorkspace.PK}`, { account: props.account, workspace: props.selectedWorkspace });
    if (response.ok) { props.getWorkspace(); }
    closePermissions();
  }
  const saveWorkspace = async (workspace: any) => {
    const response = await post(`account/update-workspace/${props.user.attributes.id}`, { account: props.account, workspace });
    if (response.ok && response.data) { setWorkspace(response.data); }
  }

  if (!loading) {
    return (
      <div>
        <div className="flex h-full">
          <div className="bg-gray-800 flex-1 min-w-0 flex items-center justify-between px-4">
            <div className="w-full mx-auto py-4 px-6 flex grow">
              <h2 className="text-lg leading-6 font-semibold text-gray-100 pr-2">
                Workspace { props.selectedWorkspace ? `${subUx}` : subUx }
              </h2>
            </div>
            { subUx == 'Settings' ? <InnerNavButtonSelected icon={AdjustmentsVerticalIcon} title="General" clickHandler={ () => setSubUx('Settings') }/> 
            : <InnerNavButton icon={AdjustmentsVerticalIcon} title="General" clickHandler={ () => setSubUx('Settings') }/> }
            { subUx == 'Team Settings' ? 
              <InnerNavButtonSelected icon={UserGroupIcon} title="Team" clickHandler={ () => setSubUx('Team Settings') }/>
            : <InnerNavButton icon={UserGroupIcon} title="Team" clickHandler={ () => setSubUx('Team Settings') }/> 
            }
            { subUx == 'Environments' ? 
              <InnerNavButtonSelected icon={CalendarIcon} title="Environments" clickHandler={ () => setSubUx('Environments') }/> 
            : <InnerNavButton icon={CalendarIcon} title="Environments" clickHandler={ () => setSubUx('Environments') }/> 
            }
            { subUx == 'Subscription' ? 
              <InnerNavButtonSelected icon={CalendarIcon} title="Subscription" clickHandler={ () => setSubUx('Subscription') }/> 
            : <InnerNavButton icon={CalendarIcon} title="Subscription" clickHandler={ () => setSubUx('Subscription') }/> 
            }     
          </div>
        </div>
        { error == "UsernameExistsException" && <DuplicateUserAlert closeAlert={ setError(null) }/> }
        { error == "PasswordNotComplexException" && <PasswordNotComplexAlert closeAlert={ setError(null) }/> }
        { error == "AccountUserLimitAlert" && <AccountUserLimitAlert closeAlert={ setError(null) }/> }
        { error == "EmailFormatException" && <EmailFormatAlert closeAlert={ setError(null) }/> }
        <dl> 
          { props.selectedWorkspace && 
          <div className="bg-gray-800 border-2 min-h-full mt-4 rounded border-cyan-300 mx-4 overflow-hidden">
            <div> 
              { subUx == 'Settings' && 
                <div className="mb-6 sm:flex sm:flex-col sm:align-center">
                  <div className="flex">
                    <h2 className="text-xl w-full p-5 text-gray-200">General Settings</h2>
                    <div className="p-4">
                      <InnerNavButtonSelected icon={ShieldCheckIcon} title="Save" clickHandler={ () => saveWorkspace(workspace) }/>
                    </div>
                  </div>
                  <div className="mx-6 border border-top border-cyan-300 mb-6"/>
                  <div className=" grid grid-cols-12 gap-2 px-4 pb-6">
                    <div className="col-span-11 text-gray-200">
                      <InputBox 
                        name="Team Name"
                        onChange={(event: any) => {event.preventDefault(); handleOrganizationValueChange(event);}}
                        placeholder={ workspace ? workspace : '' }
                        defaultValue={ workspace ? workspace.name : '' }
                      />
                    </div>

                  </div>
                  <div className="mx-6 border border-top border-cyan-300 mb-6"/>
                  <p className="mx-4 my-5 text-s text-gray-600 border-4 px-2 py-2 border-red-300 rounded">
                    <ExclamationCircleIcon className="h-6 w-6 inline text-pink-500 rounded"/>
                    <span className="pl-3 text-pink-500">You must include the Workflow Message ID Value below in the stated Workflow Message ID Location of your HL7 Messages for security.</span>
                  </p>
                  <div className=" grid grid-cols-12 gap-2 px-4 pb-6">
                    <div className="col-span-4">
                      <InputBox name="Workflow Message ID Location"
                        onChange={handleMessageIDChange}
                        placeholder={ workspace ? workspace.hash.location : '' }
                        defaultValue={ workspace ? workspace.hash.location : '' }/>
                    </div>
                    <div className="col-span-7">
                      <InputBox name="Workflow Message ID Value"
                        onChange={handleMessageIDValueChange}
                        placeholder={ workspace ? workspace.hash.value : '' }
                        defaultValue={ workspace ? workspace.hash.value : '' }/>
                    </div>
                  </div>
                </div>
              }
              { subUx == 'Team Settings' && 
                <div className="sm:flex sm:flex-col sm:align-center">
                  <h2 className="w-full text-xl p-4 text-gray-200 bg-gray-800">Owner | { props.account?.email }</h2>
                  <h2 className="w-full text-xl p-4 text-cyan-300 bg-gray-800">Members</h2>
                  <UserPermissionsModal
                    open={togglePermissions}
                    close={savePermissions}
                    updatePermissions={updatePermissions}
                    selectedUser={selectedUser}
                  />
                  <table className="mb-6 mx-2">
                    <tbody> 
                      { workspace?.accounts?.filter((account: any) => { if (account.email != workspace?.owner?.email) return account; }).map((user : any, index : number) => (
                        <tr key={ user.email + index }
                          className={ index % 2 === 0 
                            ? "border-gray-400 border-2 text-gray-200 hover:border-4 hover:border-cyan-600"
                            : "border-gray-400 border-2 text-gray-200 hover:border-4 hover:border-cyan-600"
                        }>
                        <td className="px-4 py-4 whitespace-wrap text-md font-light text-gray-200"> { user.email ? user.email : '' }</td>
                        <td className="whitespace-wrap px-2 text-sm text-gray-800">
                          <button className="w-full  bg-gray-500 py-2 hover:bg-cyan-400 text-white rounded focus:outline-none focus:shadow-outline" type="button"
                            onClick={ () => { openPermissions(user)}}>Permissions</button>
                        </td>
                        <td className="whitespace-wrap px-2 text-sm text-gray-800">
                          <button className="w-full bg-gray-500 py-2 hover:bg-pink-500 text-white rounded focus:outline-none focus:shadow-outline" type="button"
                            onClick={ () => { removeUserFromWorkspace(user); setLoadingType('remove')}}>
                            X
                          </button>
                        </td>
                      </tr>))
                    } </tbody>
                  </table>
                  <div className="mx-6 border border-top border-gray-300"/>
                  <h2 className="w-full text-xl p-4 text-pink-500 bg-gray-800">Invite</h2>
                  <table className="min-w-full">
                    <tbody>
                      <tr>
                        <td className="px-2 py-4 whitespace-nowrap text-md text-gray-200">
                          <InputBox name="First Name" onChange={handleNewUserFirstNameChange} defaultValue={ invite.firstName }/>
                        </td>
                        <td className="px-2 py-4 whitespace-nowrap text-md text-gray-200">
                          <InputBox name="Last Name" onChange={handleNewUserLastNameChange} defaultValue={ invite.lastName }/>
                        </td>
                        <td className="px-2 py-4 whitespace-nowrap text-md text-gray-200">
                          <InputBox name="Email" onChange={handleNewUserEmailChange} defaultValue={ invite.email }/>
                        </td>
                        { (!loading) && 
                        <td className="px-2 py-3 whitespace-nowrap text-md text-gray-200">
                          <button className="w-full bg-cyan-500 hover:bg-cyan-400 py-2 px-1 text-white rounded focus:outline-none focus:shadow-outline" type="button" onClick={ () => { addUserToWorkspace(props.user.attributes.id); setLoadingType('invite'); } }>
                            Send Invite
                          </button>
                        </td>
                        }
                        { (loading && loadingType == 'invite') && 
                        <td className="px-2 py-3 whitespace-nowrap text-md text-gray-200">
                          <button className="w-full bg-gray-500 hover:bg-gray-400 py-2 px-1 text-white rounded focus:outline-none focus:shadow-outline animate-bounce" type="button">
                            Sending...
                          </button>
                        </td>
                        }
                      </tr>
                    </tbody>
                  </table>
                </div>
              }
              { subUx == 'Environments' && 
                <Environments 
                  user={props.user}
                  account={props.account}
                  setAccount={props.setAccount}
                  selectedWorkspace={props.selectedWorkspace}
                  setSelectedWorkspace={props.setSelectedWorkspace}
                  setupEnvironmentsHandler={props.setupEnvironmentsHandler} 
                />
              } 
              { subUx == 'Subscription' && 
                <div>
                  <PricingBox 
                    account={props.account}
                    products={products}
                    selectProduct={selectProduct}
                    getAccount={props.getAccount}
                    awsProductURL={awsProductURL}
                    quantityPurchased={quantityPurchased}
                    setQuantityPurchased={setQuantityPurchased}
                    selectedProduct={selectedProduct}
                    getCustomerPortal={getCustomerPortal}
                    selectedWorkspace={ props.selectedWorkspace }
                    ux={billingUX}
                  />
                </div> 
              }
              { subUx == 'Release Notes' && 
                <div className="relative mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
                  <div className="px-4 sm:px-6 lg:px-8">
                    <div className="relative mx-auto max-w-[37.5rem] pt-20 text-center pb-20">
                      <h1 className="text-4xl font-extrabold tracking-tight text-gray-300 sm:text-5xl">Changelog</h1>
                      <p className="mt-4 text-base leading-7 text-cyan-400">
                        Stay up to date with all of the latest additions and improvements we've made to Retrohook. 
                        We'll be updating this page regularly with all of the latest changes and will provide a way to subscribe soon.
                      </p>
                    </div>
                  </div>
                  <section id="2022-12-15" aria-labelledby="2022-12-15-heading" className="md:flex">
                    <h2 id="2022-12-15-heading" className="pl-7 text-md leading-6 text-slate-500 md:w-1/4 md:pl-0 md:pr-6 md:text-right">
                      <a href="#2023-06-01" className="text-cyan-400">June 1st, 2023</a>
                    </h2>
                    <div className="relative py-4 mx-4">
                      <span className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-300" aria-hidden="true" />
                      <div className="relative flex space-x-2">
                        <div className="min-w-0 flex-1 flex justify-between space-x-1"></div>
                        <div className="max-w-none prose-h3:mb-4 pl-12 prose-h3:text-base prose-h3:leading-6 prose-sm prose prose-slate prose-a:font-semibold prose-a:text-pink-500 hover:prose-a:text-purple-600">
                          <h2 className="text-red-400 text-2xl">Beta Release &#127881;</h2>
                          <p className="py-4 text-gray-200">Today we're happy to announce the launch of our baseline HL7v2 tool. We've been working hard to build a platform that will help you work with HL7 messages quickly and easily. 
                            We're excited to share this with you and we hope you enjoy it! Check out our documentation or just start building with our workflow builder.
                          </p>
                          <a href="/workflows">
                            <img className="rounded-xl ring-1 ring-offset-[-1px] ring-slate-700/10" src={ScreenShot} alt="Workflow Builder" />
                          </a>
                        </div>
                      </div>
                    </div>
                  </section>
                </div>
              }
            </div>
          </div>
          } 
        </dl>
      </div>
    );
  }
  else {
    return (
      <div className="items-center animate-pulse py-48 sm:py-24 md:py-32 lg:py-48">
        <img className="h-24 mx-auto w-auto" src={logo} alt="Logo" />
        <h2 className="mt-0 text-center text-2xl font-bold text-gray-200">
          Retrohook
          <span className="inline align-middle text-xs tracking-tight text-cyan-300 pl-1"></span>
        </h2>
      </div>
    );
  }
}
